import { acceptJsonHeaders, csrfHeaders, handleJson, jsonHeaders } from "@/lib/json-request"

export type VoiceProviderKind = "elevenlabs" | "minimax"

/** Значение селектора голоса: голос героя или библиотечный голос провайдера. */
export type VoiceSelection =
  | { kind: "voice"; voice_id: number }
  | { kind: "library"; provider_kind: string; provider_voice_id: string }

export type LibraryVoiceRow = {
  provider_kind: string
  provider_voice_id: string
  name: string
  description: string | null
  preview_url: string | null
  labels: Record<string, string>
}

export type VoiceLibraryResponse = {
  voices: LibraryVoiceRow[]
  default_voice_ids: Record<string, string>
  /** Провайдеры, чей листинг сейчас недоступен: их секции неполны, ответ нельзя кешировать как успех. */
  failed_provider_kinds?: string[]
}

export type VoiceDesignRequestInfo = {
  id: number
  creation_method: "design" | "remix"
  voice_name: string | null
  prompt: string | null
  preview_text: string | null
  params: Record<string, unknown>
  provider_kind?: string
  api_payload?: Record<string, unknown>
}

export type VoiceRow = {
  id: number
  name: string
  description: string | null
  status: "draft" | "ready" | "failed"
  creation_method: "design" | "remix" | "ivc" | "import" | "library"
  provider_kind: string
  provider_voice_id: string | null
  settings: Record<string, unknown>
  source_voice_id: number | null
  design_request: VoiceDesignRequestInfo | null
  api_payload?: Record<string, unknown> | null
  active: boolean
  preview_audio_url: string | null
  created_at: string
}

export type VoicePreviewRow = {
  id: number
  duration_secs: number | null
  audio_url: string | null
  created_at: string
}

export type VoicesIndexResponse = {
  voices: VoiceRow[]
  previews: VoicePreviewRow[]
  preview_request: VoiceDesignRequestInfo | null
  active_voice_id: number | null
  voice_creation_model: string | null
  voice_provider_kind: VoiceProviderKind | string | null
}

export type VoiceDesignParamsPayload = {
  auto_generate_text?: boolean
  should_enhance?: boolean
  loudness?: number
  guidance_scale?: number
  quality?: number | null
  seed?: number | null
  prompt_strength?: number | null
  voice_id?: string | null
}

export type MinimaxIvcParamsPayload = {
  need_noise_reduction?: boolean
  need_volume_normalization?: boolean
  aigc_watermark?: boolean
  language_boost?: string | null
  prompt_text?: string | null
  preview_text?: string | null
  text_validation?: string | null
  accuracy?: number | null
}

function basePath(projectId: number, characterId: number) {
  return `/projects/${projectId}/workspace/characters/${characterId}/voices`
}

/** Библиотечные голоса провайдеров проекта — грузится лениво при открытии voice-picker. */
export async function fetchVoiceLibrary(projectId: number): Promise<VoiceLibraryResponse> {
  const res = await fetch(`/projects/${projectId}/workspace/voice_library`, {
    headers: acceptJsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<VoiceLibraryResponse>(res)
}

/** Голос из библиотеки провайдера: создаётся мгновенно, сервер сам берёт имя из каталога. */
export async function createLibraryVoice(
  projectId: number,
  characterId: number,
  payload: { provider_kind: string; provider_voice_id: string },
): Promise<{ voice: VoiceRow; active_voice_id: number | null }> {
  const res = await fetch(`${basePath(projectId, characterId)}/from_library`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(payload),
  })
  return handleJson<{ voice: VoiceRow; active_voice_id: number | null }>(res)
}

export async function listVoices(projectId: number, characterId: number): Promise<VoicesIndexResponse> {
  const res = await fetch(basePath(projectId, characterId), {
    headers: acceptJsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<VoicesIndexResponse>(res)
}

export async function requestVoicePreviews(
  projectId: number,
  characterId: number,
  payload: {
    creation_method: "design" | "remix"
    name: string
    prompt: string
    preview_text?: string | null
    source_voice_id?: number
    design_params?: VoiceDesignParamsPayload
    reference_audio?: File | null
    provider_kind?: VoiceProviderKind | string
  },
) {
  const hasFile = !!payload.reference_audio
  if (hasFile) {
    const form = new FormData()
    form.append("creation_method", payload.creation_method)
    form.append("name", payload.name)
    form.append("prompt", payload.prompt)
    if (payload.provider_kind) form.append("provider_kind", payload.provider_kind)
    if (payload.preview_text) form.append("preview_text", payload.preview_text)
    if (payload.source_voice_id != null) form.append("source_voice_id", String(payload.source_voice_id))
    if (payload.design_params) {
      for (const [key, value] of Object.entries(payload.design_params)) {
        if (value === null || value === undefined) continue
        form.append(`design_params[${key}]`, String(value))
      }
    }
    form.append("reference_audio", payload.reference_audio!)
    const res = await fetch(`${basePath(projectId, characterId)}/previews`, {
      method: "POST",
      headers: csrfHeaders(),
      credentials: "same-origin",
      body: form,
    })
    return handleJson<{ ok: boolean; message?: string }>(res)
  }

  const res = await fetch(`${basePath(projectId, characterId)}/previews`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({
      creation_method: payload.creation_method,
      name: payload.name,
      prompt: payload.prompt,
      preview_text: payload.preview_text,
      source_voice_id: payload.source_voice_id,
      design_params: payload.design_params,
      provider_kind: payload.provider_kind,
    }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function createVoiceFromPreview(
  projectId: number,
  characterId: number,
  payload: { preview_id: number; name: string; description?: string | null },
) {
  const res = await fetch(`${basePath(projectId, characterId)}/from_preview`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(payload),
  })
  return handleJson<{ ok: boolean; voice: VoiceRow; message?: string }>(res)
}

export async function createIvcVoice(
  projectId: number,
  characterId: number,
  payload: {
    name: string
    description?: string | null
    remove_background_noise?: boolean
    labels?: Partial<Record<"language" | "accent" | "gender" | "age", string>> | null
    files: File[]
    provider_kind?: VoiceProviderKind | string
    minimax_params?: MinimaxIvcParamsPayload | null
    prompt_audio?: File | null
  },
) {
  const form = new FormData()
  form.append("name", payload.name)
  if (payload.provider_kind) form.append("provider_kind", payload.provider_kind)
  if (payload.description) form.append("description", payload.description)
  if (payload.remove_background_noise) form.append("remove_background_noise", "true")
  if (payload.labels) {
    for (const [key, value] of Object.entries(payload.labels)) {
      if (value?.trim()) form.append(`labels[${key}]`, value.trim())
    }
  }
  if (payload.minimax_params) {
    for (const [key, value] of Object.entries(payload.minimax_params)) {
      if (value === null || value === undefined) continue
      form.append(`minimax_params[${key}]`, String(value))
    }
  }
  if (payload.prompt_audio) form.append("prompt_audio", payload.prompt_audio)
  payload.files.forEach((file) => form.append("files[]", file))
  const res = await fetch(`${basePath(projectId, characterId)}/ivc`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function importVoice(
  projectId: number,
  characterId: number,
  payload: {
    provider_voice_id: string
    name?: string | null
    provider_kind?: VoiceProviderKind | string
  },
) {
  const res = await fetch(`${basePath(projectId, characterId)}/import`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({
      provider_voice_id: payload.provider_voice_id,
      name: payload.name ?? null,
      provider_kind: payload.provider_kind,
    }),
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function activateVoice(projectId: number, characterId: number, voiceId: number) {
  const res = await fetch(`${basePath(projectId, characterId)}/${voiceId}/activate`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({}),
  })
  return handleJson<{ ok: boolean; active_voice_id: number; message?: string }>(res)
}

export async function updateVoice(projectId: number, characterId: number, voiceId: number, payload: { name: string }) {
  const res = await fetch(`${basePath(projectId, characterId)}/${voiceId}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ name: payload.name }),
  })
  return handleJson<{ ok: boolean; voice: VoiceRow; message?: string }>(res)
}

export async function deleteVoice(projectId: number, characterId: number, voiceId: number) {
  const res = await fetch(`${basePath(projectId, characterId)}/${voiceId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<{ ok: boolean; message?: string }>(res)
}

export async function regenerateVoicePreview(
  projectId: number,
  characterId: number,
  voiceId: number,
  payload: { text: string },
) {
  const res = await fetch(`${basePath(projectId, characterId)}/${voiceId}/regenerate_preview`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ text: payload.text }),
  })
  return handleJson<{ ok: boolean; voice: VoiceRow; message?: string }>(res)
}
